/**
 * ka-cart-drawer.js
 * KickAss Products — Custom Cart Drawer (v3)
 *
 * What this script does (and does NOT do):
 * ─────────────────────────────────────────
 * The theme's existing custom elements handle the heavy lifting:
 *   - <product-form>  intercepts the PDP add-to-cart form, calls /cart/add.js
 *                     with bundled sections, then calls CartDrawer.open()
 *   - <product-card>  handles collection card ATC buttons the same way
 *   - <cart-drawer>   handles open/close, Escape key, outside-click,
 *                     and quantity changes via .qty input change events
 *
 * This script adds the things the theme's CartDrawer does NOT handle:
 *   1. +/- quantity buttons (the theme only handles direct input changes)
 *   2. Product Recommendations — fetched via Section Rendering API so that
 *      Liquid handles all club pricing logic server-side. The theme's existing
 *      `product-recommendations` section is reused, which already outputs
 *      `data-club-price` on ATC buttons for club-priced products.
 *   3. Recommendation "Add to cart" — passes `properties[_powerx_custom_price]`
 *      when `data-club-price` is present, matching the theme's product-card
 *      club pricing contract.
 *   4. Standard Storefront Events (shopify:cart:view, shopify:cart:lines-update)
 *      for BOGOS and analytics app compatibility.
 *
 * We deliberately do NOT re-implement add-to-cart or section re-rendering —
 * the theme's JS already does that correctly.
 */

(function () {
  'use strict';

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ── Standard Storefront Events ─────────────────────────────────────── */

  const SE = (window.Shopify && window.Shopify.StandardEvents) || null;

  function dispatchCartViewEvent() {
    if (!SE || !SE.CartViewEvent) return;
    try {
      const countEl = document.querySelector('.thb-secondary-area--item-count');
      const itemCount = parseInt((countEl && countEl.textContent) || '0', 10);
      const drawerEl = document.getElementById('Cart-Drawer');
      if (drawerEl) {
        drawerEl.dispatchEvent(new SE.CartViewEvent({
          context: 'dialog',
          cart: itemCount > 0 ? { totalQuantity: itemCount } : null,
        }));
      }
    } catch (e) { /* non-fatal */ }
  }

  /* ── Recommendations via Section Rendering API ──────────────────────── */
  /*
   * We fetch the theme's existing `product-recommendations` section via the
   * Storefront section rendering endpoint. This means Liquid runs server-side
   * and has full access to variant metafields (powerx.custom_price), the
   * customer object, and cart attributes — so club pricing is rendered
   * correctly without any client-side metafield lookups.
   *
   * The returned HTML contains the theme's product-card custom elements with
   * `data-club-price` on the ATC buttons for club-priced products, which our
   * rec add handler reads below.
   */

  async function loadRecommendations() {
    const container = document.querySelector('[data-cart-recommendations]');
    if (!container) return;

    const productId = container.dataset.productId;
    if (!productId) return;

    // Avoid re-fetching if already populated for this product
    if (container.dataset.loaded === productId) return;

    try {
      // Use the Section Rendering API to get server-rendered product cards
      // with full Liquid access to metafields and customer state.
      const url = ROOT + 'recommendations/products'
        + '?product_id=' + productId
        + '&limit=5'
        + '&intent=related'
        + '&section_id=product-recommendations';

      const res = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!res.ok) return;

      const html = await res.text();

      // Parse the section HTML and extract the product cards
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const cards = doc.querySelectorAll('product-card');

      if (!cards.length) {
        container.innerHTML = '';
        return;
      }

      // Build our slim rec list using data from the server-rendered cards.
      // We extract: image, title, href, price block, and ATC button data.
      const titleHtml = '<h3 class="ka-cart-recommendations__title">We also recommend</h3>';
      const items = [];

      cards.forEach(function (card) {
        // Image
        const imgEl = card.querySelector('.product-card-image-link img');
        const imgSrc = imgEl ? (imgEl.getAttribute('src') || '') : '';
        const imgAlt = imgEl ? (imgEl.getAttribute('alt') || '') : '';

        // Title / link
        const titleEl = card.querySelector('.product-card-title');
        const title = titleEl ? titleEl.textContent.trim() : '';
        const href = titleEl ? (titleEl.getAttribute('href') || '#') : '#';

        // Price block — reuse the server-rendered ka-card-price-box directly
        const priceBox = card.querySelector('.ka-card-price-box');
        const priceHtml = priceBox ? priceBox.outerHTML : '';

        // ATC button data
        const atcBtn = card.querySelector('.product-card--add-to-cart-button');
        const productHandle = atcBtn ? (atcBtn.dataset.productHandle || '') : '';
        const clubPrice = atcBtn ? (atcBtn.dataset.clubPrice || '') : '';
        // Get variant id from the product JSON (we need it for /cart/add.js)
        // Store it as a data attribute on our button
        const productId2 = atcBtn ? (atcBtn.dataset.productId || '') : '';

        if (!title || !productHandle) return;

        // GWP / club credit badge — reuse the server-rendered markup directly
        const gwpWrap = card.querySelector('.ka-card-gwp-mini-wrap');
        const gwpHtml = gwpWrap ? gwpWrap.outerHTML : '';

        items.push(
          '<li class="ka-cart-rec-item">'
          + '<a href="' + href + '" class="ka-cart-rec-item__image-link" tabindex="-1" aria-hidden="true">'
          + (imgSrc ? '<img src="' + imgSrc + '" alt="' + imgAlt.replace(/"/g, '&quot;') + '" width="64" height="64" loading="lazy" class="ka-cart-rec-item__image">' : '<div class="ka-cart-rec-item__image"></div>')
          + '</a>'
          + '<div class="ka-cart-rec-item__info">'
          // Title is now a clickable link
          + '<a href="' + href + '" class="ka-cart-rec-item__title">' + title.replace(/</g, '&lt;').replace(/>/g, '&gt;') + '</a>'
          // GWP / club credit badge (only present if product has the metafield set)
          + gwpHtml
          + priceHtml
          + '</div>'
          + '<button type="button" class="ka-cart-rec-item__add-btn" data-rec-add'
          + ' data-product-handle="' + productHandle + '"'
          + (clubPrice ? ' data-club-price="' + clubPrice + '"' : '')
          + (productId2 ? ' data-product-id="' + productId2 + '"' : '')
          + ' aria-label="Add ' + title.replace(/"/g, '&quot;') + ' to cart">Add</button>'
          + '</li>'
        );
      });

      if (!items.length) {
        container.innerHTML = '';
        return;
      }

      container.innerHTML = titleHtml + '<ul class="ka-cart-rec-list">' + items.join('') + '</ul>';
      container.dataset.loaded = productId;

    } catch (err) {
      container.innerHTML = '';
    }
  }

  /* ── Quantity +/- button handler ────────────────────────────────────── */
  /*
   * The theme's CartDrawer.onChange() fires when a .qty input's value changes.
   * Our +/- buttons update the input value and dispatch a native "change" event,
   * which the theme's handler picks up — no duplicate AJAX calls needed.
   */

  function handleQtyButtonClick(e) {
    const incBtn = e.target.closest('[data-qty-inc]');
    const decBtn = e.target.closest('[data-qty-dec]');
    if (!incBtn && !decBtn) return;

    const selector = e.target.closest('.ka-cart-item__qty-selector');
    if (!selector) return;

    const input = selector.querySelector('.ka-cart-item__qty-input');
    if (!input) return;

    const current = parseInt(input.value, 10) || 0;
    const newVal = incBtn ? current + 1 : Math.max(0, current - 1);

    input.value = newVal;
    // Dispatch a native change event so CartDrawer.onChange() picks it up
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ── Recommendation "Add" button ────────────────────────────────────── */
  /*
   * Matches the theme's product-card club pricing contract:
   *   - Fetches the first available variant ID from /products/{handle}.json
   *   - Appends `properties[_powerx_custom_price]` when data-club-price is set
   *   - Uses bundled section rendering so the drawer re-renders in one round trip
   */

  async function handleRecAdd(btn) {
    const productHandle = btn.dataset.productHandle;
    if (!productHandle) return;

    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = '...';

    try {
      // Resolve the first available variant ID
      const productRes = await fetch(ROOT + 'products/' + productHandle + '.json', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const productData = await productRes.json();
      const variant = (productData.product.variants || []).find(function (v) {
        return v.available !== false;
      }) || productData.product.variants[0];

      if (!variant) {
        btn.textContent = 'Unavailable';
        setTimeout(function () { btn.textContent = originalText; btn.disabled = false; }, 2000);
        return;
      }

      const variantId = variant.id;
      const clubPrice = btn.dataset.clubPrice || '';

      // Get sections to render from the CartDrawer element
      const cartDrawer = document.getElementById('Cart-Drawer');
      const sectionsToRender = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
        ? cartDrawer.getSectionsToRender().map(function (s) { return s.section; })
        : ['cart-drawer', 'cart-bubble'];

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      // Pass club price as a hidden line property — matches the theme's contract
      if (clubPrice) {
        formData.append('properties[_powerx_custom_price]', clubPrice);
      }
      formData.append('sections', sectionsToRender.join(','));
      formData.append('sections_url', window.location.pathname);

      const res = await fetch(ROOT + 'cart/add.js', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript',
        },
        body: formData,
      });

      const data = await res.json();

      if (data.status) {
        btn.textContent = 'Error';
        setTimeout(function () { btn.textContent = originalText; btn.disabled = false; }, 2000);
        return;
      }

      // Re-render the drawer sections using the CartDrawer's own method if available
      if (cartDrawer && data.sections) {
        cartDrawer.getSectionsToRender().forEach(function (section) {
          const el = document.getElementById(section.id);
          if (!el || !data.sections[section.section]) return;
          const target = el.querySelector(section.selector) || el;
          const parser = new DOMParser();
          const doc = parser.parseFromString(data.sections[section.section], 'text/html');
          const selected = doc.querySelector(section.selector);
          if (selected) target.innerHTML = selected.innerHTML;
        });
        if (typeof cartDrawer.removeProductEvent === 'function') {
          cartDrawer.removeProductEvent();
        }
      }

      // Dispatch Standard Storefront Event for BOGOS / analytics
      if (SE && SE.CartLinesUpdateEvent) {
        try {
          const cartRes = await fetch(ROOT + 'cart.js');
          const cart = await cartRes.json();
          const deferred = SE.CartLinesUpdateEvent.createPromise();
          if (cartDrawer) {
            cartDrawer.dispatchEvent(new SE.CartLinesUpdateEvent({
              action: 'add',
              context: 'dialog',
              lines: [{ merchandiseId: String(variantId), quantity: 1 }],
              promise: deferred.promise,
            }));
          }
          deferred.resolve({ cart: SE.CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
        } catch (e) { /* non-fatal */ }
      }

      btn.textContent = 'Added!';
      // Reset recommendations so they reload with the updated cart context
      const recContainer = document.querySelector('[data-cart-recommendations]');
      if (recContainer) delete recContainer.dataset.loaded;
      setTimeout(function () { btn.textContent = originalText; btn.disabled = false; }, 1500);

    } catch (err) {
      btn.textContent = originalText;
      btn.disabled = false;
    }
  }

  /* ── Event listeners ────────────────────────────────────────────────── */

  function attachListeners() {
    // Qty +/- buttons (delegated — survives re-renders)
    document.addEventListener('click', handleQtyButtonClick);

    // Recommendation "Add" buttons (delegated)
    document.addEventListener('click', function (e) {
      const addBtn = e.target.closest('[data-rec-add]');
      if (addBtn) handleRecAdd(addBtn);
    });

    // Bundle toggle buttons (delegated — survives re-renders)
    // Components are server-rendered by Liquid — the toggle only shows/hides.
    document.addEventListener('click', function (e) {
      const toggleBtn = e.target.closest('[data-bundle-toggle]');
      if (!toggleBtn) return;

      const listId = toggleBtn.getAttribute('aria-controls');
      const list = document.getElementById(listId);
      if (!list) return;

      const isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';
      toggleBtn.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
      list.hidden = isExpanded;
    });

    // Load recommendations when the drawer opens.
    // The theme fires a custom "cart-drawer:open" event from CartDrawer.open().
    document.addEventListener('cart-drawer:open', function () {
      dispatchCartViewEvent();
      loadRecommendations();
    });

    // Also load recommendations when cart:item-added fires (drawer opens after ATC)
    document.addEventListener('cart:item-added', function () {
      // Small delay to let the re-render complete first
      setTimeout(loadRecommendations, 150);
    });
  }

  /* ── Init ───────────────────────────────────────────────────────────── */

  function init() {
    attachListeners();

    // Pre-load recommendations if cart already has items on page load
    const recContainer = document.querySelector('[data-cart-recommendations]');
    if (recContainer && recContainer.dataset.productId) {
      loadRecommendations();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
